/// <amd-module name="TFS/Admin/Views/AdminHub/AdminHubMasterDetail" />
import React = require("react");

import { AdminHubBreadcrumbService } from "TFS/Admin/Views/AdminHub/AdminHubBreadcrumbService";
import { ISettingsSearchComponentProps } from "TFS/Admin/Views/SettingsSearch/admin-settings-search.Props";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { ITfsPageData, ITfsPageService } from "Tfs/Platform/Page";
import { IObservableValue, ObservableCollection, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { getTabs, ILegacyContributedTab, processTabContribution } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, RegionComponent, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { Button } from "VSSUI/Button";
import { Icon, IconSize, IIconProps } from "VSSUI/Icon";
import { IListItemDetails, IListRow, List, ListItem, ListSelection } from "VSSUI/List";
import { ListBoxItemType } from "VSSUI/ListBox";
import { DetailsPanel, MasterPanel, MasterPanelHeader } from "VSSUI/MasterDetails";
import {
    BaseMasterDetailsContext,
    bindSelectionToObservable,
    IMasterDetailsContext,
    IMasterDetailsContextLayer,
    MasterDetailsContext
} from "VSSUI/MasterDetailsContext";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ITabGroup as VssUiTabGroup, IVssContributedTab } from "VSSUI/Tabs";
import { TextField, TextFieldStyle, TextFieldWidth } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, KeyCode } from "VSSUI/Util";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";

import AdminResources = require("TFS/Admin/Views/AdminHub/Resources");

interface IAdminHubComponentProps {
    // Load pivots (tabs) that target tab groups that target this id
    id: string;
    level: "project" | "collection" | "user";
    // Data provider with data about legacy hub contributions
    hubContributionDataProvider?: string;
    // Id of the default pivot
    defaultPivot?: string;
    displayTabIcons?: boolean;
}

interface ITabGroup {
    id: string;
    name: string;
    rank: number;
}

interface IContributionData {
    contributions: {
        id: string;
        properties: ILegacyContributedTab;
    }[];
}

class AdminHubMasterDetailComponent extends VssComponent<IAdminHubComponentProps, {}> {
    private static readonly AdminHubSettingsSearchEnabledFeatureId = "ms.vss-admin-web.organization-admin-settings-search-feature";
    private static readonly ContributedGroupKey = "contributed";
    private static readonly PivotParamName = "adminPivot";

    private _name = ""; // e.g. "Account Settings" or "Project Settings" or "Collection Settings"
    private _entityName = ""; // Name of the Project, Account or Collection
    private _selectedTabId = new ObservableValue<string>("");
    private _groups: VssUiTabGroup[] = [];
    private _breadcrumbService: AdminHubBreadcrumbService;
    private _contributionMap: { [id: string]: IVssContributedTab } = {};
    private _isSearchEnabled = false;
    private _isSearching = new ObservableValue<boolean>(false);
    private _searchValue = new ObservableValue<string>("");

    private _masterDetailsContext: IMasterDetailsContext;
    private _itemProvider: GroupedItemProvider;

    private textField = React.createRef<TextField>();

    constructor(props: IAdminHubComponentProps, context: IVssComponentContext) {
        super(props, context);
        const { pageContext } = context;
        const service =
            props.level == "project"
                ? pageContext.getService<ITfsPageService>("ITfsPageService")
                : pageContext.getService<IVssPageService>("IVssPageService");
        switch (props.level) {
            case "collection":
                {
                    const pageData = service.getData() as IPageData;
                    if (pageData) {
                        this._entityName = pageData.hostName;
                        this._name = pageData.isHosted ? AdminResources.AccountSettings : AdminResources.CollectionSettings;
                    }
                }
                break;
            case "project":
                {
                    const pageData = service.getData() as ITfsPageData;
                    this._name = AdminResources.ProjectSettings;
                    if (pageData && pageData.project) {
                        this._entityName = pageData.project.name;
                    }
                }
                break;
            case "user":
                {
                    const pageData = service.getData() as IPageData;
                    this._name = AdminResources.UserSettings;
                    if (pageData && pageData.user) {
                        this._entityName = pageData.user.displayName;
                    }
                }
                break;
        }

        this._breadcrumbService = pageContext.getService<AdminHubBreadcrumbService>("admin-hub-header-breadcrumb-service");
        this._selectedTabId.value = this.getCurrentTab();
        this.initialize();
    }

    public render(): JSX.Element {
        return (
            <MasterDetailsContext.Provider value={this._masterDetailsContext}>
                <div className="flex-row" style={{ width: "100%" }}>
                    <ScreenSizeObserver>
                        {(screenSizeProps: { screenSize: ScreenSize }) => {
                            const isSmall = screenSizeProps.screenSize <= ScreenSize.small;
                            return <MasterPanel className={!isSmall ? "admin-stackpanel" : undefined} />;
                        }}
                    </ScreenSizeObserver>
                    <DetailsPanel className="admin-hub bolt-page v-scroll-auto flex-row" />
                </div>
            </MasterDetailsContext.Provider>
        );
    }

    public componentDidMount() {
        super.componentDidMount();
        this.updatePage(this._selectedTabId.value);
    }

    private onTextChanged = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        this._searchValue.value = value;
    };

    private onClickClearButton = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this._searchValue.value = "";
        this.textField.current && this.textField.current.focus();
    };

    private clearButtonIconProps: IIconProps = {
        render: () =>
            this._searchValue.value ? (
                <Button
                    className="bolt-text-filterbaritem-clear search-clear-button"
                    iconProps={{ iconName: "Cancel" }}
                    ariaLabel={AdminResources.CancelButton}
                    onClick={this.onClickClearButton}
                />
            ) : (
                <div />
            )
    };

    private SearchField: React.FunctionComponent<{}> = (props) => {
        const context = React.useContext(MasterDetailsContext);
        return (
            <div className="setting-search-textbox" role="search">
                <TextField
                    ariaLabel={AdminResources.SearchSettings}
                    className="bolt-text-filterbaritem flex-grow"
                    inputClassName="search-text-field-input"
                    onChange={this.onTextChanged}
                    width={TextFieldWidth.auto}
                    onKeyDown={(ev) => {
                        switch (ev.which) {
                            case KeyCode.enter:
                                this._isSearching.value = true;
                                context.setDetailsPanelVisbility(true);
                                break;
                            case KeyCode.escape:
                                this._isSearching.value = false;
                                break;
                            default:
                                return;
                        }
                        ev.preventDefault();
                        ev.stopPropagation();
                    }}
                    maxLength={50}
                    placeholder={AdminResources.SearchSettings}
                    ref={this.textField}
                    style={TextFieldStyle.inline}
                    suffixIconProps={this.clearButtonIconProps}
                    value={this._searchValue}
                    prefixIconProps={{ className: "keyword-filter-icon", iconName: "Search" }}
                />
            </div>
        );
    };

    private getCurrentTab(): string {
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        return navigationService.getRouteValues()[AdminHubMasterDetailComponent.PivotParamName] || this.props.defaultPivot || "";
    }

    private initialize() {
        const { pageContext } = this.context;
        const { hubContributionDataProvider } = this.props;
        const service = pageContext.getService<IVssContributionService>("IVssContributionService");
        const tabIds = service.getContributionChildren(this.props.id, "ms.vss-web.tab-group") || [];
        const collection: IVssContributedTab[] = [];
        const loadingComponent = () => (
            <div className="absolute-fill flex flex-column flex-center justify-center">
                <Spinner size={SpinnerSize.large} />
            </div>
        );

        this._groups = tabIds.reduce((result: VssUiTabGroup[], id: string) => {
            const tabs = getTabs(pageContext, id, {
                routeStateKey: AdminHubMasterDetailComponent.PivotParamName,
                fpsRequired: true,
                preProcessTabContribution: (tab) => this.preProcessContributedTab(tab, id)
            });
            collection.push(...tabs.value.sort((a, b) => (a.order || 1000) - (b.order || 1000)));
            const tabGroup = service.getContribution<ITabGroup>(id);
            tabGroup && result.push({ id, name: tabGroup.name, order: tabGroup.rank });
            return result;
        }, []);
        this._groups.push({
            id: AdminHubMasterDetailComponent.ContributedGroupKey,
            name: AdminResources.Extensions,
            order: 999
        });

        if (hubContributionDataProvider) {
            const data = service.getData<IContributionData>(hubContributionDataProvider);
            const tabs = new ObservableCollection<IVssContributedTab>();
            data &&
                tabs.push(data.contributions, (c) =>
                    processTabContribution(
                        pageContext,
                        {
                            routeStateKey: AdminHubMasterDetailComponent.PivotParamName,
                            fpsRequired: true,
                            loadingComponent,
                            preProcessTabContribution: (tab) => {
                                this.preProcessContributedTab(tab, AdminHubMasterDetailComponent.ContributedGroupKey);
                            }
                        },
                        c.properties,
                        c.id
                    )
                );
            collection.push(...tabs.value.sort((a, b) => (a.order || 1000) - (b.order || 1000)));
        }

        collection.forEach((tab) => {
            this._contributionMap[tab.id] = tab;
        });
        if (!this._contributionMap[this._selectedTabId.value]) {
            // if tab doesn't exists set
            this._selectedTabId.value = collection[0].id;
        }
        this._isSearchEnabled =
            this.props.level === "collection" &&
            isFeatureEnabled(this.context.pageContext, AdminHubMasterDetailComponent.AdminHubSettingsSearchEnabledFeatureId, false);
        this._itemProvider = new GroupedItemProvider(
            collection,
            this._groups.sort((a, b) => (a.order || 1000) - (b.order || 1000)),
            true
        );

        this._masterDetailsContext = new BaseMasterDetailsContext(this.settingsHubPayload(), () => {});
    }

    private preProcessContributedTab(tab: IVssContributedTab | ILegacyContributedTab, groupId: string) {
        const legacyTab = tab as ILegacyContributedTab;
        if (!legacyTab.id && legacyTab.itemKey) {
            legacyTab.id = legacyTab.itemKey;
        }
        if (!legacyTab.groupId) {
            legacyTab.groupId = groupId;
        }
        if (this.props.displayTabIcons !== true) {
            legacyTab.iconProps = undefined;
        }
    }

    private updatePage = (tabId: string) => {
        const item = this._contributionMap[tabId];
        if (!item || !item.name) {
            return;
        }
        const navigation = this.context.pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
        navigation.setPageTitle(`${AdminResources.Settings} · ${item.name}${this._entityName && ` (${this._entityName})`}`);
        this._breadcrumbService.setSelectedPivotBreadcrumb(ObservableLike.getValue(item.name), item.url && ObservableLike.getValue(item.url));
    };

    private renderSettingsItem = (index: number, item: any, details: IListItemDetails<any>): JSX.Element => {
        if (item.type === ListBoxItemType.Divider) {
            return <tr className="admin-tab-item-divider"></tr>;
        }
        const textContent = (
            <Tooltip overflowOnly={true}>
                <span className={`${item.type === ListBoxItemType.Header && `title-xs`}`}>{item.name || item.text}</span>
            </Tooltip>
        );
        let content = textContent;
        if (item.iconProps || item.followingRegionName) {
            content = (
                <React.Fragment>
                    {item.iconProps && <span className="flex-column">{Icon({ ...item.iconProps, className: css("icon-margin", item.iconProps.className) })}</span>}
                    <span className="flex-column">{textContent}</span>
                    {item.followingRegionName && <RegionComponent className="flex-column" componentRegion={item.followingRegionName} />}
                </React.Fragment>
            );
        }
        const isHeader = item.type === ListBoxItemType.Header;
        return (
            <ListItem index={index} details={details} className={css("admin-tab-item", !isHeader && "admin-tab")}>
                <div className={css("bolt-tab-inner-container flex-row flex-grow admin-tab-text", isHeader && "admin-tab-header")}>{content}</div>
            </ListItem>
        );
    };

    private settingsHubPayload = (): IMasterDetailsContextLayer<IVssContributedTab, undefined> => ({
        ariaLabel: this._name,
        key: "admin-hub",
        masterPanelContent: {
            renderContent: (parentItem, selectedItem) => <this.AdminPanelContent selectedItem={selectedItem} listAriaLabel={this._name} />,
            renderHeader: () => {
                const subtitle = (
                    <Tooltip overflowOnly={true}>
                        <div className="text-ellipsis">{this._entityName}</div>
                    </Tooltip>
                );
                return <MasterPanelHeader title={this._name} subTitle={subtitle} titleAriaLevel={2} />;
            },
            renderSearch: this._isSearchEnabled ? () => <this.SearchField /> : undefined,
            hideBackButton: true
        },
        detailsContent: {
            renderContent: (item) => <this.AdminHubDetailsView tab={item} />
        },
        selectedMasterItem: new ObservableValue<IVssContributedTab>(this._contributionMap[this._selectedTabId.value]),
        parentItem: undefined
    });

    private AdminPanelContent: React.FunctionComponent<{ selectedItem: IObservableValue<IVssContributedTab>; listAriaLabel: string }> = (props) => {
        const [itemProvider] = React.useState(this._itemProvider);
        const [selection] = React.useState(
            new ListSelection({
                selectOnFocus: false,
                unselectableRanges: this._itemProvider.value.reduce((a: any[], item: any, index: number) => {
                    !item.url && a.push({ beginIndex: index, endIndex: index });
                    return a;
                }, [])
            })
        );

        React.useEffect(() => {
            bindSelectionToObservable(selection, itemProvider, props.selectedItem);
        });

        const onSelect = (ev: React.SyntheticEvent<HTMLElement, Event>, listRow: IListRow<any>) => {
            const { url } = listRow.data;
            url && listRow.data.onBeforeTabChange(ev, "", url);
            ev.preventDefault();
        };
        return (
            <List
                ariaLabel={props.listAriaLabel}
                itemProvider={itemProvider}
                selection={selection}
                renderRow={this.renderSettingsItem}
                width="100"
                onActivate={onSelect}
                onSelect={onSelect}
                virtualize={false}
            />
        );
    };

    private AdminHubDetailsView: React.FunctionComponent<{ tab: IVssContributedTab }> = (props) => {
        const context = React.useContext(MasterDetailsContext);
        const { tab } = props;
        const content = <>{tab.render && tab.render()}</>;
        return (
            <>
                <ScreenSizeObserver>
                    {(screenSizeProps: { screenSize: ScreenSize }) => {
                        const showButton = screenSizeProps.screenSize <= ScreenSize.small;
                        return (
                            showButton && (
                                <Button
                                    className="show-stack-btn"
                                    ariaLabel={AdminResources.ShowSideMenu}
                                    iconProps={{ iconName: "Back", size: IconSize.medium }}
                                    onClick={() => context.setDetailsPanelVisbility(false)}
                                />
                            )
                        );
                    }}
                </ScreenSizeObserver>
                <Observer isSearching={this._isSearching}>
                    {(props: { isSearching: boolean }) =>
                        props.isSearching ? (
                            <WrappedComponent<ISettingsSearchComponentProps>
                                dependencies={["ms.vss-admin-web.settings-search-component"]}
                                wrappedType={"ms.vss-admin-web.setting-search-page"}
                                wrappedProps={{
                                    pageContext: this.context.pageContext,
                                    searchTerm: this._searchValue.value
                                }}
                            />
                        ) : (
                            <div className="flex-column flex-grow v-scroll-auto relative">{content}</div>
                        )
                    }
                </Observer>
            </>
        );
    };
}

Components.add("adminhub-masterdetail", AdminHubMasterDetailComponent);